import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth'

interface AdminPatient {
  id: string
  full_name: string
  status: 'active' | 'inactive' | 'discharged' | 'deleted'
  condition_description?: string | null
  birth_date?: string | null
  created_at: string
  _count: {
    reports: number
    insights: number
  }
}

async function fetchAdminPatients(organizationId: string): Promise<AdminPatient[]> {
  const { data: patients, error } = await supabase
    .from('patients')
    .select('*')
    .eq('organization_id', organizationId)
    .neq('status', 'deleted')
    .order('created_at', { ascending: false })

  if (error) throw error

  // Fetch counts for each patient
  const patientsWithCounts = await Promise.all(
    (patients ?? []).map(async (patient) => {
      const [reportsCount, insightsCount] = await Promise.all([
        supabase
          .from('daily_reports')
          .select('id', { count: 'exact', head: true })
          .eq('patient_id', patient.id),
        supabase
          .from('key_insights')
          .select('id', { count: 'exact', head: true })
          .eq('patient_id', patient.id),
      ])

      return {
        ...patient,
        _count: {
          reports: reportsCount.count ?? 0,
          insights: insightsCount.count ?? 0,
        },
      }
    }),
  )

  return patientsWithCounts
}

export function useAdminPatients() {
  const { currentOrganization } = useAuth()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: ['admin-patients', organizationId],
    queryFn: () => fetchAdminPatients(organizationId!),
    enabled: !!organizationId,
  })
}

export function useUpdatePatientStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      patientId,
      status,
    }: {
      patientId: string
      status: 'active' | 'inactive' | 'discharged'
    }) => {
      const { error } = await supabase
        .from('patients')
        .update({
          status,
          updated_at: new Date().toISOString(),
        })
        .eq('id', patientId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-patients'] })
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] })
      queryClient.invalidateQueries({ queryKey: ['patients'] })
    },
  })
}

export function useDeletePatient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (patientId: string) => {
      const { error } = await supabase
        .from('patients')
        .update({
          status: 'deleted',
          updated_at: new Date().toISOString(),
        })
        .eq('id', patientId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-patients'] })
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] })
      queryClient.invalidateQueries({ queryKey: ['patients'] })
    },
  })
}
